<template>
  <main
    class="message-board"
    aria-labelledby="message-board-title"
  >
    <header class="page-header">
      <div>
        <h1 id="message-board-title">
          Nachrichten
        </h1>
        <p class="muted">
          Nachrichten zu Meldungen von Eltern
        </p>
      </div>
    </header>

    <div class="board-shell">
      <section
        :class="['thread-panel', { 'thread-panel--hidden-mobile': selected }]"
        aria-label="Unterhaltungen"
      >
        <div class="thread-tools">
          <label class="search-field">
            <span class="sr-only">Nach Schüler oder Absender suchen</span>
            <i
              class="pi pi-search"
              aria-hidden="true"
            />
            <input
              v-model.trim="search"
              class="input soft-input"
              type="search"
              placeholder="Schüler oder Absender suchen"
            >
          </label>
          <div
            class="filter-tabs"
            role="group"
            aria-label="Nachrichten filtern"
          >
            <button
              type="button"
              :class="['filter-tab', { active: filter === 'all' }]"
              @click="filter = 'all'"
            >
              Alle
            </button>
            <button
              type="button"
              :class="['filter-tab', { active: filter === 'unread' }]"
              @click="filter = 'unread'"
            >
              Ungelesen
              <span
                v-if="unreadTotal"
                class="filter-count"
              >{{ unreadTotal }}</span>
            </button>
          </div>
        </div>

        <div
          v-if="loading"
          class="panel-state muted"
        >
          Nachrichten werden geladen …
        </div>
        <div
          v-else-if="error"
          class="panel-state notice notice-danger"
          role="alert"
        >
          {{ error }}
        </div>
        <div
          v-else-if="!filteredThreads.length"
          class="panel-state muted"
        >
          {{ search || filter === 'unread' ? 'Keine passenden Nachrichten.' : 'Keine Nachrichten vorhanden.' }}
        </div>

        <div
          v-else
          class="thread-list"
        >
          <article
            v-for="thread in filteredThreads"
            :key="thread.threadId"
            :class="['thread-row', { unread: thread.hasUnread, selected: selected?.threadId === thread.threadId }]"
          >
            <button
              class="thread-main"
              type="button"
              @click="openThread(thread)"
            >
              <span class="thread-title-row">
                <strong>{{ thread.studentName || '—' }}</strong>
                <span
                  v-if="thread.hasUnread"
                  class="unread-dot"
                  title="Ungelesen"
                />
              </span>
              <span class="thread-field"><span>Absender:</span> {{ thread.senderName || '—' }}</span>
              <span class="thread-field"><span>Grund:</span> {{ thread.subject || '—' }}</span>
              <span class="thread-dates">
                <span><span>Meldung:</span> {{ formatDateTime(thread.notificationCreatedAt) }}</span>
                <span><span>Nachricht:</span> {{ formatDateTime(thread.updatedAt) }}</span>
              </span>
            </button>
            <div class="thread-actions">
              <button
                class="icon-btn"
                type="button"
                title="Nachrichtenverlauf öffnen"
                aria-label="Nachrichtenverlauf öffnen"
                @click="openThread(thread)"
              >
                <i
                  class="pi pi-eye"
                  aria-hidden="true"
                />
              </button>
              <button
                v-if="!thread.hasUnread"
                class="icon-btn"
                type="button"
                title="Als ungelesen markieren"
                aria-label="Als ungelesen markieren"
                @click="markUnread(thread)"
              >
                <i
                  class="pi pi-envelope"
                  aria-hidden="true"
                />
              </button>
            </div>
          </article>
        </div>
      </section>

      <section
        :class="['conversation-panel', { 'conversation-panel--visible-mobile': selected }]"
        aria-label="Nachrichtenverlauf"
      >
        <div
          v-if="!selected"
          class="conversation-empty muted"
        >
          <i
            class="pi pi-envelope"
            aria-hidden="true"
          />
          <p>Wähle eine Nachricht aus.</p>
        </div>

        <template v-else>
          <div class="notification-summary">
            <button
              class="mobile-back icon-btn"
              type="button"
              title="Zurück zur Nachrichtenliste"
              aria-label="Zurück zur Nachrichtenliste"
              @click="closeThread"
            >
              <i
                class="pi pi-arrow-left"
                aria-hidden="true"
              />
            </button>
            <button
              class="notification-open icon-btn"
              type="button"
              title="Meldung öffnen"
              aria-label="Meldung öffnen"
              @click="openNotificationView"
            >
              <i
                class="pi pi-folder-open"
                aria-hidden="true"
              />
            </button>
            <div class="summary-grid">
              <div><span>Grund der Meldung:</span><strong>{{ selected.subject || '—' }}</strong></div>
              <div><span>Zeitraum:</span><strong>{{ formatRange(selected) }}</strong></div>
              <div><span>Schüler:</span><strong>{{ selected.studentName || '—' }}</strong></div>
              <div><span>Absender:</span><strong>{{ selected.senderName || '—' }}</strong></div>
              <div><span>gesendet am:</span><strong>{{ formatDateTime(selected.notificationCreatedAt) }}</strong></div>
              <div>
                <span>Status am:</span>
                <strong>{{ statusLabel(selected.status) }} — {{ formatDateTime(selected.statusUpdatedAt) }}</strong>
              </div>
            </div>
          </div>

          <div
            ref="messageList"
            class="message-list"
            role="log"
            aria-live="polite"
          >
            <p
              v-if="messagesLoading"
              class="muted"
            >
              Verlauf wird geladen …
            </p>
            <p
              v-else-if="conversationError"
              class="notice notice-danger"
              role="alert"
            >
              {{ conversationError }}
            </p>
            <template v-else>
              <div
                v-for="message in messages"
                :key="message.id"
                :class="['message-row', isStaffMessage(message) ? 'staff' : 'parent']"
              >
                <div class="message-bubble">
                  <div class="message-meta">
                    <strong>{{ message.senderName || '—' }}</strong>
                    <span>{{ formatDateTime(message.createdAt) }}</span>
                  </div>
                  <p>{{ message.message }}</p>
                </div>
              </div>
            </template>
          </div>

          <form
            class="reply-box"
            @submit.prevent="sendReply"
          >
            <label
              class="sr-only"
              for="notification-message-reply"
            >Antwort</label>
            <textarea
              id="notification-message-reply"
              v-model="reply"
              class="input soft-input"
              rows="3"
              maxlength="5000"
              placeholder="Antwort schreiben …"
              :disabled="sending"
              @keydown.ctrl.enter.prevent="sendReply"
              @keydown.meta.enter.prevent="sendReply"
            />
            <button
              class="btn btn-primary"
              type="submit"
              :disabled="!reply.trim() || sending"
            >
              <i
                class="pi pi-send"
                aria-hidden="true"
              />
              {{ sending ? 'Wird gesendet …' : 'Senden' }}
            </button>
          </form>
        </template>
      </section>
    </div>

    <NotificationViewDialog
      :open="notificationViewOpen"
      :notification-id="notificationViewId"
      @close="closeNotificationView"
      @create-correction="onCreateCorrection"
    />

    <NotificationCreateDialog
      :open="notificationCreateOpen"
      :notif-parent-id="correctionParentId"
      :prefill-student-id="correctionPrefill.studentId"
      :prefill-subject="correctionPrefill.subject"
      :prefill-date-from="correctionPrefill.dateFrom"
      :prefill-date-to="correctionPrefill.dateTo"
      :prefill-time-from="correctionPrefill.timeFrom"
      :prefill-time-to="correctionPrefill.timeTo"
      :prefill-kind="correctionPrefill.kind"
      @close="closeCreateDialog"
      @created="onCorrectionCreated"
    />
  </main>
</template>

<script setup>
import {computed, nextTick, onMounted, ref} from 'vue';
import {
  fetchNotificationChat,
  fetchNotificationMessageThreads,
  markNotificationChatRead,
  markNotificationChatUnread,
  postNotificationChatMessage
} from '@/api/notifications';
import NotificationCreateDialog from '@/components/notifications/NotificationCreateDialog.vue';
import NotificationViewDialog from '@/components/notifications/NotificationViewDialog.vue';

const threads = ref([]);
const loading = ref(false);
const error = ref('');
const search = ref('');
const filter = ref('all');
const selected = ref(null);
const messages = ref([]);
const messagesLoading = ref(false);
const conversationError = ref('');
const reply = ref('');
const sending = ref(false);
const messageList = ref(null);
const notificationViewOpen = ref(false);
const notificationViewId = ref(null);
const notificationCreateOpen = ref(false);
const correctionParentId = ref(null);
const correctionPrefill = ref({
  studentId: null,
  subject: '',
  dateFrom: null,
  dateTo: null,
  timeFrom: null,
  timeTo: null,
  kind: 'daily'
});

const unreadTotal = computed(() => threads.value.filter(item => item.hasUnread).length);

const filteredThreads = computed(() => {
  const needle = search.value.toLocaleLowerCase('de-DE');
  return threads.value
      .filter(item => {
        if (filter.value === 'unread' && !item.hasUnread) return false;
        if (!needle) return true;
        return String(item.studentName || '').toLocaleLowerCase('de-DE').includes(needle)
            || String(item.senderName || '').toLocaleLowerCase('de-DE').includes(needle);
      })
      .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());
});

onMounted(loadThreads);

async function loadThreads() {
  loading.value = true;
  error.value = '';
  try {
    threads.value = await fetchNotificationMessageThreads();
  } catch (e) {
    error.value = e?.response?.data?.message || 'Nachrichten konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

async function openThread(thread) {
  selected.value = thread;
  messages.value = [];
  conversationError.value = '';
  messagesLoading.value = true;
  try {
    messages.value = await fetchNotificationChat(thread.threadId);
    await markNotificationChatRead(thread.threadId);
    setThreadRead(thread.threadId);
    await scrollToEnd();
  } catch (e) {
    conversationError.value = e?.response?.data?.message || 'Der Nachrichtenverlauf konnte nicht geladen werden.';
  } finally {
    messagesLoading.value = false;
    await scrollToEnd();
  }
}

function closeThread() {
  selected.value = null;
  messages.value = [];
  reply.value = '';
  conversationError.value = '';
}

function openNotificationView() {
  notificationViewId.value = selected.value?.notificationId ?? selected.value?.threadId ?? null;
  if (notificationViewId.value != null) notificationViewOpen.value = true;
}

async function closeNotificationView() {
  const selectedThreadId = selected.value?.threadId ?? null;
  notificationViewOpen.value = false;
  notificationViewId.value = null;
  await loadThreads();
  if (selectedThreadId != null) {
    selected.value = threads.value.find(thread => thread.threadId === selectedThreadId) ?? selected.value;
  }
}

async function onCreateCorrection(notification) {
  notificationViewOpen.value = false;
  await nextTick();
  createCorrection(notification);
}

function createCorrection(notification) {
  const source = notification || {};
  const fallback = selected.value || {};
  const parentId = source.id ?? source.notificationId ?? source.notification_id
      ?? fallback.notificationId ?? fallback.notification_id ?? fallback.threadId ?? null;
  const dateFrom = source.dateFrom ?? source.date_from ?? fallback.dateFrom ?? fallback.date_from ?? null;
  const dateTo = source.dateTo ?? source.date_to ?? fallback.dateTo ?? fallback.date_to ?? null;
  const timeFrom = source.timeFrom ?? source.time_from ?? fallback.timeFrom ?? fallback.time_from ?? null;
  const timeTo = source.timeTo ?? source.time_to ?? fallback.timeTo ?? fallback.time_to ?? null;

  correctionParentId.value = parentId;
  correctionPrefill.value = {
    studentId: source.studentId ?? source.student_id ?? fallback.studentId ?? fallback.student_id ?? null,
    subject: source.subject ?? fallback.subject ?? '',
    dateFrom: dateFrom || null,
    dateTo: dateTo || (timeFrom || timeTo ? dateFrom || null : null),
    timeFrom: timeFrom || null,
    timeTo: timeTo || null,
    kind: (source.kind ?? fallback.kind) === 'time' || timeFrom || timeTo ? 'time' : 'daily'
  };
  notificationCreateOpen.value = true;
}

function closeCreateDialog() {
  notificationCreateOpen.value = false;
}

async function onCorrectionCreated() {
  const selectedThreadId = selected.value?.threadId ?? null;
  notificationCreateOpen.value = false;
  await loadThreads();
  if (selectedThreadId != null) {
    selected.value = threads.value.find(thread => thread.threadId === selectedThreadId) ?? selected.value;
  }
}

async function markUnread(thread) {
  try {
    await markNotificationChatUnread(thread.threadId);
    thread.hasUnread = true;
    thread.unreadCount = Math.max(1, Number(thread.unreadCount || 0));
    if (selected.value?.threadId === thread.threadId) selected.value = {...thread};
  } catch (e) {
    error.value = e?.response?.data?.message || 'Die Nachricht konnte nicht als ungelesen markiert werden.';
  }
}

async function sendReply() {
  const text = reply.value.trim();
  if (!selected.value || !text || sending.value) return;
  sending.value = true;
  conversationError.value = '';
  try {
    const created = await postNotificationChatMessage(selected.value.threadId, text);
    if (created) messages.value.push(created);
    reply.value = '';
    const now = created?.createdAt || new Date().toISOString();
    const row = threads.value.find(item => item.threadId === selected.value.threadId);
    if (row) {
      row.updatedAt = now;
      row.hasUnread = false;
      row.unreadCount = 0;
    }
    selected.value = row || {...selected.value, updatedAt: now, hasUnread: false, unreadCount: 0};
    await scrollToEnd();
  } catch (e) {
    conversationError.value = e?.response?.data?.message || 'Die Antwort konnte nicht gesendet werden.';
  } finally {
    sending.value = false;
  }
}

function setThreadRead(threadId) {
  const row = threads.value.find(item => item.threadId === threadId);
  if (row) {
    row.hasUnread = false;
    row.unreadCount = 0;
    selected.value = row;
  }
}

async function scrollToEnd() {
  await nextTick();
  if (messageList.value) messageList.value.scrollTop = messageList.value.scrollHeight;
}

function isStaffMessage(message) {
  return [1, 2].includes(Number(message?.senderRoleId ?? message?.sender_role_id));
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

function formatDate(value) {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  return formatDateTime(value);
}

function formatTime(value) {
  if (!value) return '';
  const match = String(value).match(/^(\d{2}):(\d{2})/);
  if (match) return `${match[1]}:${match[2]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit'
  }).format(date);
}

function formatRange(thread) {
  if (thread.kind === 'time') {
    const date = thread.dateFrom || thread.timeFrom;
    const from = formatTime(thread.timeFrom);
    const to = formatTime(thread.timeTo);
    return `${formatDate(date)}${from ? `, ${from}${to ? `–${to}` : ''} Uhr` : ''}`;
  }
  const from = formatDate(thread.dateFrom);
  const to = formatDate(thread.dateTo || thread.dateFrom);
  return from === to ? from : `${from}–${to}`;
}

function statusLabel(status) {
  const labels = {offen: 'Offen', gelesen: 'Gelesen', akzeptiert: 'Akzeptiert', abgelehnt: 'Abgelehnt'};
  return labels[String(status || '').toLowerCase()] || status || '—';
}
</script>

<style scoped>
.message-board {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: calc(100dvh - 97px);
  min-height: 680px;
}

.message-board > .page-header {
  flex: 0 0 auto;
  margin-bottom: 0;
}

.page-header h1 {
  margin: 0;
}

.page-header p {
  margin: 4px 0 0;
}

.board-shell {
  display: grid;
  flex: 1 1 auto;
  grid-template-columns:minmax(360px, 42%) minmax(0, 1fr);
  min-height: 600px;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  background: var(--surface, #fff);
}

.thread-panel .input {
  width: 100%;
  padding: 6px 10px 6px 40px;;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
}

.thread-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-right: 1px solid var(--border);
}

.thread-tools {
  flex: 0 0 auto;
  padding: 14px;
  border-bottom: 1px solid var(--border);
  background: #fcfcfc;
}

.search-field {
  position: relative;
  display: block;
}

.search-field i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
}

.search-field input {
  width: 100%;
  padding-left: 36px;
}

.filter-tabs {
  display: flex;
  gap: 6px;
  margin-top: 10px;
}

.filter-tab {
  border: 1px solid var(--border);
  background: transparent;
  color: inherit;
  border-radius: 999px;
  padding: 6px 12px;
  cursor: pointer;
}

.filter-tab.active {
  border-color: var(--primary);
  background: color-mix(in srgb, var(--primary) 12%, transparent);
  color: var(--primary);
}

.filter-count {
  display: inline-flex;
  justify-content: center;
  min-width: 20px;
  margin-left: 4px;
  padding: 1px 5px;
  border-radius: 10px;
  background: var(--primary);
  color: #fff;
  font-size: 12px;
}

.thread-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.thread-row {
  display: grid;
  grid-template-columns:minmax(0, 1fr) auto;
  border-bottom: 1px solid var(--border);
  background: var(--surface, #fff);
}

.thread-row.unread {
  background: color-mix(in srgb, var(--info) 8%, var(--surface, #fff));
}

.thread-row.selected {
  box-shadow: inset 3px 0 0 var(--primary);
}

.thread-main {
  min-width: 0;
  padding: 14px;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.thread-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 16px;
}

.unread-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--info);
}

.thread-field {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 3px;
  font-size: 14px;
}

.thread-field > span, .thread-dates span > span {
  color: var(--muted);
  font-weight: 500;
}

.thread-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 8px;
  color: var(--muted);
  font-size: 12px;
}

.thread-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 10px 10px 10px 0;
}

.icon-btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface, #fff);
  color: var(--text);
  cursor: pointer;
}

.icon-btn:hover {
  border-color: var(--primary);
  color: var(--primary);
}

.conversation-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: color-mix(in srgb, var(--border) 16%, var(--surface, #fff));
}

.conversation-empty {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.conversation-empty i {
  font-size: 36px;
}

.notification-summary {
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
  padding: 16px 18px;
  border-bottom: 1px solid var(--border);
  background: color-mix(in srgb, var(--primary) 7%, var(--surface, #fff));
}

.notification-open {
  position: absolute;
  top: 12px;
  right: 14px;
  z-index: 1;
}

.summary-grid {
  display: grid;
  grid-template-columns:repeat(2, minmax(0, 1fr));
  gap: 3px 24px;
  padding-right: 42px;
}

.summary-grid > div {
  display: flex;
  gap: 7px;
  min-width: 0;
  font-size: 12px;
}

.summary-grid span {
  color: var(--muted);
  white-space: nowrap;
}

.summary-grid strong {
  overflow-wrap: anywhere;
}

.summary-status {
  grid-column: 1 / -1;
}

.message-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 22px;
}

.message-row {
  display: flex;
  margin-bottom: 14px;
}

.message-row.staff {
  justify-content: flex-end;
}

.message-bubble {
  max-width: min(680px, 82%);
  padding: 11px 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface, #fff);
}

.message-row.staff .message-bubble {
  border-color: color-mix(in srgb, var(--primary) 25%, var(--border));
  background: color-mix(in srgb, var(--primary) 9%, var(--surface, #fff));
}

.message-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 6px 18px;
  margin-bottom: 6px;
  font-size: 12px;
}

.message-meta span {
  color: var(--muted);
}

.message-bubble p {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 20px;
}

.reply-box {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-end;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid var(--border);
  background: color-mix(in srgb, var(--primary) 7%, var(--surface, #fff));
}

.reply-box textarea {
  flex: 1;
  min-width: 0;
  resize: vertical;
  max-height: 180px;
}

.reply-box .btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.panel-state {
  margin: 16px;
}

.mobile-back {
  display: none;
  margin-bottom: 10px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 900px) {
  .board-shell {
    display: block;
  }

  .thread-panel {
    height: 100%;
    border-right: 0;
  }

  .thread-panel--hidden-mobile {
    display: none;
  }

  .conversation-panel {
    display: none;
    height: 100%;
  }

  .conversation-panel--visible-mobile {
    display: flex;
  }

  .mobile-back {
    display: inline-flex;
  }

  .summary-grid {
    grid-template-columns:1fr;
  }

  .summary-status {
    grid-column: auto;
  }
}

@media (max-width: 560px) {
  .notification-summary {
    padding: 12px;
  }

  .notification-open {
    top: 10px;
    right: 10px;
  }

  .message-list {
    padding: 14px 10px;
  }

  .message-bubble {
    max-width: 92%;
  }

  .reply-box {
    align-items: stretch;
    flex-direction: column;
    padding: 10px;
  }

  .reply-box .btn {
    justify-content: center;
  }
}
</style>
